import React, { useEffect, useState } from "react";
import {
  ThemeProvider,
  createTheme,
  Box,
  Grid,
  TextField,
  Button,
  Typography,
  Paper,
  MenuItem,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Switch,
  Divider,
} from "@mui/material";
import CustomSnackbar from "../../newTestPrep/components/ui-component/snackbar/CustomSnackbar";
import TimelineTable from "./SAP_TimelineList";

/* ================= THEME ================= */
const theme = createTheme({
  palette: {
    primary: { main: "#4CAF50" },
    background: { default: "#F1F8F4" },
  },
  components: {
    MuiTextField: {
      defaultProps: {
        variant: "outlined",
        fullWidth: true,
        size: "small",
      },
    },
  },
});

/* ================= DROPDOWNS ================= */
const quarters = ["Q1", "Q2", "Q3", "Q4"];
const modes = ["Online", "Offline", "Hybrid"];
const certificate = ["Yes", "No"];
const assessment = ["Yes", "No"];

/* ================= ID MAPPINGS ================= */
const classMap = {
  "Class 9": 1,
  "Class 10": 2,
  "Class 11": 3,
  "Class 12": 4,
};
const quarterMap = { Q1: 1, Q2: 2, Q3: 3, Q4: 4 };
const yesNoMap = { Yes: 1, No: 2 };
const modeMap = { Online: 1, Offline: 2, Hybrid: 3 };

/* ================= COMPONENT ================= */
export default function SAP_Timeline() {
  const API_ENDPOINT = process.env.REACT_APP_API_ENDPOINT;

  const [formData, setFormData] = useState({
    subActivities: [],
  });
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [refreshKey, setRefreshKey] = useState(0);
  const [streams, setStreams] = useState([]);
  const [newStream, setNewStream] = useState("");
  const [categories, setCategories] = useState([]);
  const [activityTypes, setActivityTypes] = useState([]);
  const [platforms, setPlatforms] = useState([]);
  const [newCategory, setNewCategory] = useState("");
  const [newActivityType, setNewActivityType] = useState("");
  const [newPlatform, setNewPlatform] = useState("");

  const showSnackbar = (message, severity) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const filteredClasses = Object.entries(classMap).filter(([label, value]) => {
    if (!formData.student_enrolled_for) return true; // show all initially
    return value >= formData.student_enrolled_for;
  });

  const activities = [
    { name: "Timeline Presentation", quarter: "Q1" },
    {
      name: "Personality Development Session",
      subActivities: ["Time Management", "Goal Setting", "Growth Mindset"],
      quarter: "Q1",
    },
    { name: "Community Service Work", quarter: "Q2" },
    {
      name: "Book Reading",
      subActivities: ["To Kill a Mockingbird", "The Alchemist", "Sapiens"],
      quarter: "Q3",
    },
  ];

  const excelFields = [
    {
      name: "quarter",
      label: "Quarter",
      type: "select",
      options: quarters,
      required: true,
    },
    { name: "activityName", label: "Activity Name", required: true },

    {
      name: "certificate",
      label: "Certificate",
      type: "select",
      options: certificate,
    },
    {
      name: "assessment",
      label: "Assessment",
      type: "select",
      options: assessment,
    },
    {
      name: "mode",
      label: "Mode",
      type: "select",
      options: modes,
      required: true,
    },
    {
      name: "activityDescription",
      label: "Activity Description",
      multiline: true,
      required: true,
    },
  ];

  const handleActivityChange = (value) => {
    const selected = activities.find((a) => a.name === value);
    setSelectedActivity(selected || null);

    setFormData((prev) => ({
      ...prev,
      activityName: value,
      subActivities: selected?.subActivities
        ? [
            {
              name: "",
              description: "",
              duration: "",
              link: "",
              assessmentCount: "",
            },
          ]
        : [],
    }));
  };

  const handleChange = (field, value) => {
    setFormData((prev) => {
      let updatedData = { ...prev, [field]: value };

      // Reset class if "Student Enrolled for" changes
      if (field === "student_enrolled_for") {
        updatedData.class_id = "";
      }

      return updatedData;
    });
  };

  const addSubActivity = () =>
    setFormData((prev) => ({
      ...prev,
      subActivities: [
        ...prev.subActivities,
        {
          name: "",
          description: "",
          duration: "",
          link: "",
          assessmentCount: "",
          isMandatory: false,
          projectLanguage: "",
          toolsRequired: "",
        },
      ],
    }));

  const updateSubActivity = (i, f, v) => {
    const updated = [...formData.subActivities];
    updated[i][f] = v;
    setFormData({ ...formData, subActivities: updated });
  };

  const removeSubActivity = (i) =>
    setFormData({
      ...formData,
      subActivities: formData.subActivities.filter((_, idx) => idx !== i),
    });

  useEffect(() => {
    fetchMasters();
  }, []);

  const fetchMasters = async () => {
    try {
      const [streamsRes, categoriesRes, activityRes, platformRes] =
        await Promise.all([
          fetch(`${API_ENDPOINT}timeline/show-all-streams`),
          fetch(`${API_ENDPOINT}timeline/show-all-categories`),
          fetch(`${API_ENDPOINT}timeline/show-all-activity-types`),
          fetch(`${API_ENDPOINT}timeline/show-all-platforms`),
        ]);

      const streamsData = await streamsRes.json();
      const categoriesData = await categoriesRes.json();
      const activityData = await activityRes.json();
      const platformData = await platformRes.json();

      setStreams(
        streamsData.data.map((s) => ({
          id: s.id,
          label: s.stream_name,
        })),
      );

      setCategories(
        categoriesData.data.map((c) => ({
          id: c.id,
          label: c.category_name,
        })),
      );

      setActivityTypes(
        activityData.data.map((a) => ({
          id: a.id,
          label: a.activity_type,
        })),
      );

      setPlatforms(
        platformData.data.map((p) => ({
          id: p.id,
          label: p.platform_name,
        })),
      );
    } catch (err) {
      console.error(err);
      showSnackbar("Failed to load master data", "error");
    }
  };

  const addStream = async () => {
    if (!newStream.trim()) return;

    try {
      const res = await fetch(`${API_ENDPOINT}timeline/add-stream-name`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          stream_name: newStream.trim(),
          user_id: 1,
        }),
      });

      const data = await res.json();

      if (res.status === 201) {
        setStreams((prev) => [
          ...prev,
          { id: data.data.id, label: data.data.stream_name },
        ]);

        handleChange("stream", data.data.id);
        setNewStream("");
        showSnackbar("Stream added successfully", "success");
      }
    } catch (err) {
      showSnackbar("Failed to add stream", "error");
    }
  };

  const addCategory = async () => {
    if (!newCategory.trim()) return;

    try {
      const res = await fetch(`${API_ENDPOINT}timeline/add-category-name`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          category_name: newCategory.trim(),
          user_id: 1,
        }),
      });

      const data = await res.json();

      if (res.status === 201) {
        setCategories((prev) => [
          ...prev,
          { id: data.data.id, label: data.data.category_name },
        ]);

        handleChange("category", data.data.id);
        setNewCategory("");
        showSnackbar("Category added successfully", "success");
      }
    } catch {
      showSnackbar("Failed to add category", "error");
    }
  };
  const addActivityType = async () => {
    if (!newActivityType.trim()) return;

    try {
      const res = await fetch(`${API_ENDPOINT}timeline/add-activity-type`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          activity_type: newActivityType.trim(),
          user_id: 1,
        }),
      });

      const data = await res.json();

      if (res.status === 201) {
        setActivityTypes((prev) => [
          ...prev,
          { id: data.data.id, label: data.data.activity_type },
        ]);

        handleChange("activityType", data.data.id);
        setNewActivityType("");
        showSnackbar("Activity type added successfully", "success");
      }
    } catch {
      showSnackbar("Failed to add activity type", "error");
    }
  };
  const addPlatform = async () => {
    if (!newPlatform.trim()) return;

    try {
      const res = await fetch(`${API_ENDPOINT}timeline/add-platform-name`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          platform_name: newPlatform.trim(),
          user_id: 1,
        }),
      });

      const data = await res.json();

      if (res.status === 201) {
        setPlatforms((prev) => [
          ...prev,
          { id: data.data.id, label: data.data.platform_name },
        ]);

        handleChange("platform", data.data.id);
        setNewPlatform("");
        showSnackbar("Platform added successfully", "success");
      }
    } catch {
      showSnackbar("Failed to add platform", "error");
    }
  };

  const validateForm = () => {
    // Main mandatory fields
    const requiredFields = [
      "class_id",
      "stream",
      "quarter",
      "category",
      "activityName",
      "activityDescription",
      "mode",
    ];

    for (const field of requiredFields) {
      if (!formData[field]) {
        showSnackbar(`${field.replace("_", " ")} is required`, "error");
        return false;
      }
    }

    // Sub-activities validation
    for (let i = 0; i < formData.subActivities.length; i++) {
      const sub = formData.subActivities[i];

      if (!sub.name || !sub.description || !sub.link) {
        showSnackbar(
          `Sub Activity ${i + 1}: Name, Description and Link are required`,
          "error",
        );
        return false;
      }
    }

    return true;
  };

  /* ================= API CALL ================= */
  const handleSubmit = async () => {
    if (!validateForm()) return;

    const payload = {
      student_enrolled_for: formData.student_enrolled_for,
      class_id: formData.class_id,
      stream: formData.stream,
      quarter: quarterMap[formData.quarter],
      category: formData.category,
      activityName: formData.activityName,
      activityDescription: formData.activityDescription,
      activityType: formData.activityType,
      platform: formData.platform,
      certificate: yesNoMap[formData.certificate],
      assessment: yesNoMap[formData.assessment],
      mode: modeMap[formData.mode],
      project_name: formData.projectName,
      project_description: formData.projectDescription,
      project_language: formData.projectLanguage,
      tools_required: formData.toolsRequired,
      subActivities: formData.subActivities.map((s) => ({
        name: s.name,
        description: s.description,
        duration: String(s.duration),
        link: s.link,
        assessmentCount: String(s.assessmentCount),
        is_mandatory: s.isMandatory ? 1 : 0,
        tools_required: s.tools_required,
        project_language: s.project_language,
      })),
    };

    try {
      const res = await fetch(`${API_ENDPOINT}timeline/add-timeline-tasks`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await res.json();
      if (res.status === 201) {
        showSnackbar("Timeline Activity Added Successfully ", "success");
        setFormData({
          subActivities: [],
        });
        setSelectedActivity(null);
        setRefreshKey((prev) => prev + 1);
        return;
      }
      if (!res.ok) throw new Error(data.message);

      alert("Timeline Activity Added Successfully ");
    } catch (err) {
      console.error(err);
      alert("API Error ❌");
    }
  };

  /* ================= UI ================= */
  return (
    <ThemeProvider theme={theme}>
      <CustomSnackbar
        open={snackbarOpen}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
        severity={snackbarSeverity}
      />
      {/* ADD */}
      <Box sx={{ minHeight: "100vh" }}>
        <Paper sx={{ p: 4, maxWidth: 1400, mx: "auto" }}>
          <Typography variant="h5" color="primary" gutterBottom>
            Timeline Activity Form
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                select
                required
                InputLabelProps={{
                  sx: {
                    "& .MuiFormLabel-asterisk": {
                      color: "red",
                    },
                  },
                }}
                label="Student Enrolled for"
                value={formData.student_enrolled_for || ""}
                onChange={(e) =>
                  handleChange("student_enrolled_for", e.target.value)
                }
              >
                {Object.entries(classMap).map(([label, value]) => (
                  <MenuItem key={value} value={value}>
                    {label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField
                select
                required
                InputLabelProps={{
                  sx: {
                    "& .MuiFormLabel-asterisk": {
                      color: "red",
                    },
                  },
                }}
                label="Class"
                value={formData.class_id || ""}
                onChange={(e) => handleChange("class_id", e.target.value)}
              >
                {filteredClasses.map(([label, value]) => (
                  <MenuItem key={value} value={value}>
                    {label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={6}>
              <TextField
                select
                required
                label="Stream"
                value={formData.stream || ""}
                onChange={(e) => handleChange("stream", e.target.value)}
                InputLabelProps={{
                  sx: { "& .MuiFormLabel-asterisk": { color: "red" } },
                }}
                SelectProps={{
                  MenuProps: {
                    disableAutoFocusItem: true,
                    onKeyDownCapture: (e) => {
                      e.stopPropagation();
                    },
                  },
                }}
              >
                {/* Existing streams */}
                {streams.map((s) => (
                  <MenuItem key={s.id} value={s.id}>
                    {s.label}
                  </MenuItem>
                ))}

                <Divider />

                {/* Add new option */}
                <MenuItem
                  disableRipple
                  disableTouchRipple
                  sx={{ cursor: "default" }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <Box display="flex" gap={1} width="100%">
                    <TextField
                      size="small"
                      autoFocus
                      placeholder="Enter stream"
                      value={newStream}
                      onChange={(e) => setNewStream(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                    />
                    <Button
                      size="small"
                      variant="contained"
                      onClick={(e) => {
                        e.stopPropagation();

                        addStream();
                      }}
                    >
                      Save
                    </Button>
                  </Box>
                </MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField
                select
                required
                label="Category"
                value={formData.category || ""}
                onChange={(e) => handleChange("category", e.target.value)}
                InputLabelProps={{
                  sx: { "& .MuiFormLabel-asterisk": { color: "red" } },
                }}
                SelectProps={{
                  MenuProps: {
                    disableAutoFocusItem: true,
                    onKeyDownCapture: (e) => {
                      e.stopPropagation();
                    },
                  },
                }}
              >
                {categories.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.label}
                  </MenuItem>
                ))}

                <Divider />

                <MenuItem
                  disableRipple
                  disableTouchRipple
                  sx={{ cursor: "default" }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <Box display="flex" gap={1} width="100%">
                    <TextField
                      size="small"
                      autoFocus
                      placeholder="Add category"
                      value={newCategory}
                      onChange={(e) => setNewCategory(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                    />
                    <Button
                      size="small"
                      variant="contained"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!newCategory.trim()) return;

                        const newId = categories.length + 1;
                        setCategories((prev) => [
                          ...prev,
                          { label: newCategory.trim(), id: newId },
                        ]);

                        handleChange("category", newId);
                        setNewCategory("");
                        addCategory();
                      }}
                    >
                      Save
                    </Button>
                  </Box>
                </MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField
                select
                label="Activity Type"
                value={formData.activityType || ""}
                onChange={(e) => handleChange("activityType", e.target.value)}
                SelectProps={{
                  MenuProps: {
                    disableAutoFocusItem: true,
                    onKeyDownCapture: (e) => {
                      e.stopPropagation();
                    },
                  },
                }}
              >
                {activityTypes.map((a) => (
                  <MenuItem key={a.id} value={a.id}>
                    {a.label}
                  </MenuItem>
                ))}

                <Divider />

                <MenuItem disableRipple onClick={(e) => e.stopPropagation()}>
                  <Box display="flex" gap={1} width="100%">
                    <TextField
                      size="small"
                      placeholder="Add activity type"
                      value={newActivityType}
                      onChange={(e) => setNewActivityType(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                    />
                    <Button
                      size="small"
                      variant="contained"
                      onClick={(e) => {
                        e.stopPropagation();

                        addActivityType();
                      }}
                    >
                      Save
                    </Button>
                  </Box>
                </MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField
                select
                label="Platform"
                value={formData.platform || ""}
                onChange={(e) => handleChange("platform", e.target.value)}
                SelectProps={{
                  MenuProps: {
                    disableAutoFocusItem: true,
                    onKeyDownCapture: (e) => {
                      e.stopPropagation();
                    },
                  },
                }}
              >
                {platforms.map((p) => (
                  <MenuItem key={p.id} value={p.id}>
                    {p.label}
                  </MenuItem>
                ))}

                <Divider />

                <MenuItem disableRipple onClick={(e) => e.stopPropagation()}>
                  <Box display="flex" gap={1} width="100%">
                    <TextField
                      size="small"
                      placeholder="Add platform"
                      value={newPlatform}
                      onChange={(e) => setNewPlatform(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                    />
                    <Button
                      size="small"
                      variant="contained"
                      onClick={(e) => {
                        e.stopPropagation();

                        addPlatform();
                      }}
                    >
                      Save
                    </Button>
                  </Box>
                </MenuItem>
              </TextField>
            </Grid>
            {excelFields.map((f) => (
              <Grid item xs={6} key={f.name}>
                <TextField
                  fullWidth
                  required={f.required}
                  select={f.type === "select"}
                  multiline={f.multiline}
                  rows={f.multiline ? 3 : 1}
                  label={f.label}
                  value={formData[f.name] || ""}
                  onChange={(e) =>
                    f.name === "activityName"
                      ? handleActivityChange(e.target.value)
                      : handleChange(f.name, e.target.value)
                  }
                  InputLabelProps={{
                    sx: {
                      "& .MuiFormLabel-asterisk": {
                        color: "red", //  force red star
                      },
                    },
                  }}
                >
                  {f.options?.map((o) => (
                    <MenuItem key={o} value={o}>
                      {o}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            ))}
          </Grid>

          <Grid item xs={12}>
            {formData.subActivities.length > 0 && (
              <TableContainer component={Paper}>
                <Table
                  size="small"
                  sx={{
                    mt: 2,
                    "& th, & td": {
                      padding: "6px 8px",
                      fontSize: "0.8rem",
                    },
                  }}
                >
                  <TableHead>
                    <TableRow sx={{ backgroundColor: "#F1F8F4" }}>
                      <TableCell>#</TableCell>
                      <TableCell>Mandatory</TableCell>
                      <TableCell>
                        Sub Activity Name{" "}
                        <span style={{ color: "red" }}>*</span>
                      </TableCell>
                      <TableCell>Duration</TableCell>
                      <TableCell>
                        Description <span style={{ color: "red" }}>*</span>
                      </TableCell>
                      <TableCell>Project Language</TableCell>
                      <TableCell>Tools Required</TableCell>

                      <TableCell>
                        Link <span style={{ color: "red" }}>*</span>
                      </TableCell>
                      <TableCell align="center">No. of Assessment</TableCell>
                      <TableCell align="center">Action</TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {formData.subActivities.map((sub, index) => (
                      <TableRow key={index}>
                        <TableCell>{index + 1}</TableCell>
                        <TableCell align="center">
                          <Switch
                            checked={sub.isMandatory}
                            onChange={(e) =>
                              updateSubActivity(
                                index,
                                "isMandatory",
                                e.target.checked,
                              )
                            }
                            color="primary"
                          />
                        </TableCell>

                        <TableCell sx={{ minWidth: 160 }}>
                          <TextField
                            value={sub.name}
                            onChange={(e) =>
                              updateSubActivity(index, "name", e.target.value)
                            }
                            fullWidth
                          />
                        </TableCell>

                        <TableCell>
                          <TextField
                            type="number"
                            value={sub.duration}
                            inputProps={{
                              min: 0,
                              step: 1,
                            }}
                            onChange={(e) => {
                              const value = e.target.value;

                              if (/^\d*$/.test(value)) {
                                updateSubActivity(index, "duration", value);
                              }
                            }}
                          />
                        </TableCell>

                        <TableCell>
                          <TextField
                            multiline
                            rows={2}
                            value={sub.description}
                            onChange={(e) =>
                              updateSubActivity(
                                index,
                                "description",
                                e.target.value,
                              )
                            }
                          />
                        </TableCell>

                        <TableCell>
                          <TextField
                            required
                            value={sub.project_language}
                            placeholder="Eg: JavaScript"
                            onChange={(e) =>
                              updateSubActivity(
                                index,
                                "project_language",
                                e.target.value,
                              )
                            }
                            InputLabelProps={{
                              sx: {
                                "& .MuiFormLabel-asterisk": { color: "red" },
                              },
                            }}
                          />
                        </TableCell>

                        <TableCell>
                          <TextField
                            required
                            value={sub.tools_required}
                            placeholder="Eg: React, Node, MySQL"
                            onChange={(e) =>
                              updateSubActivity(
                                index,
                                "tools_required",
                                e.target.value,
                              )
                            }
                            InputLabelProps={{
                              sx: {
                                "& .MuiFormLabel-asterisk": { color: "red" },
                              },
                            }}
                          />
                        </TableCell>

                        <TableCell>
                          <TextField
                            value={sub.link}
                            onChange={(e) =>
                              updateSubActivity(index, "link", e.target.value)
                            }
                          />
                        </TableCell>

                        <TableCell>
                          <TextField
                            type="number"
                            inputProps={{ min: 0 }}
                            value={sub.assessmentCount}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (value === "" || Number(value) >= 0) {
                                updateSubActivity(
                                  index,
                                  "assessmentCount",
                                  value,
                                );
                              }
                            }}
                          />
                        </TableCell>

                        <TableCell align="center">
                          <Button
                            color="error"
                            size="small"
                            onClick={() => removeSubActivity(index)}
                          >
                            Remove
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            <Button
              variant="outlined"
              size="small"
              sx={{ mt: 4 }}
              onClick={addSubActivity}
            >
              + Add Sub Activity
            </Button>
          </Grid>
          <Box sx={{ textAlign: "right" }}>
            <Button
              variant="contained"
              color="primary"
              sx={{ color: "#fff" }}
              onClick={handleSubmit}
            >
              Save Activity
            </Button>
          </Box>
        </Paper>
      </Box>
      {/* TABLE */}
      <TimelineTable refreshKey={refreshKey} />
    </ThemeProvider>
  );
}
